import Data from '@/data'
import Store from '@/store'
import { CallbackWrap } from '@/types/common'
import { AscendancyEmbed, Embed } from '@/types/embed'
import { LevelingEmbed, LevelingOverview } from '@/types/leveling'
import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Select } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import { assets_path } from 'common'
import { EditableText } from 'components/editor/EditableText'
import React from 'react'
import { EmbedRef, useEmbed } from '../common'
import './leveling-title-box.scss'

export function TitleIcon({
  embed,
  overviewAscendancy
}: {
  embed: LevelingEmbed
  overviewAscendancy?: EmbedRef<AscendancyEmbed>
}) {
  let iconFileName = embed?.overview?.icon
  const charClass = overviewAscendancy ? Data.ascendancyClass[overviewAscendancy.embed.ascendancy] : undefined
  if (charClass) {
    iconFileName = `${assets_path}/static/media/Icon${charClass}_${overviewAscendancy!.embed.ascendancy}.webp`
  }

  return (
    <span className="poe-title-icon">
      <img src={iconFileName} />
    </span>
  )
}

const poeLeagues = [
  {
    id: '3.23',
    name: 'Affliction'
  },
  {
    id: '3.24',
    name: 'Necropolis'
  },
  {
    id: '3.25',
    name: 'Settlers of Kalguur'
  },
  {
    id: '3.26',
    name: 'Mercenaries of Trarthus'
  }
]

export function LevelingTitleBox({
  embed,
  onEmbedChange,
  stores,
  type
}: {
  embed: LevelingEmbed
  onEmbedChange?: (value: CallbackWrap<Embed>) => void
  stores: Record<string, Store>
  type?: string
}) {
  const overviewAscendancy = useEmbed(stores, embed.overview?.ascendancy, 'ascendancy')

  const onOverviewAttrChange = <K extends keyof LevelingOverview>(newValue: LevelingOverview[K], attr: K) =>
    onEmbedChange?.((embed) => {
      if (embed.type !== 'leveling') return embed
      return {
        ...embed,
        overview: {
          ...embed.overview,
          [attr]: newValue
        }
      }
    })

  const [isEditing, setIsEditing] = React.useState(false)
  const activeLeague = poeLeagues.find((league) => league.id === embed?.overview?.league)

  return (
    <div className={classNames('poe-title-box-widget', type === 'sm' && 'poe-title-box-sm')}>
      <div className="poe-title-image">
        <EditableText
          value={embed?.overview?.icon ?? ''}
          onChange={
            onEmbedChange
              ? (newValue) => {
                  onOverviewAttrChange(newValue, 'icon')
                }
              : undefined
          }
          placeholder={'IMG or empty for asc icon'}
          buttonPlaceholder={<TitleIcon embed={embed} overviewAscendancy={overviewAscendancy} />}>
          <TitleIcon embed={embed} overviewAscendancy={overviewAscendancy} />
        </EditableText>
      </div>

      <div className="poe-title-details">
        <h2 className="poe-title">
          <EditableText
            value={embed.overview.title ?? ''}
            onChange={
              onEmbedChange
                ? (newValue) => {
                    onOverviewAttrChange(newValue, 'title')
                  }
                : undefined
            }
          />
        </h2>
        <div className="poe-league">
          {onEmbedChange &&
            (isEditing ? (
              <div>
                <button onClick={() => setIsEditing(false)}>Close</button>
              </div>
            ) : (
              <div>
                <FontAwesomeIcon
                  icon={faEdit}
                  onClick={() => {
                    setIsEditing(!isEditing)
                  }}
                />
              </div>
            ))}

          {isEditing ? (
            <Select
              className="poe-commons-lib-select"
              value={embed?.overview?.league ?? ''}
              options={[
                { value: undefined, label: `SELECT LEAGUE` },
                ...poeLeagues.map((league) => ({
                  value: league.id,
                  label: `${league.name} ${league.id}`
                }))
              ]}
              onChange={
                onEmbedChange
                  ? (newValue) => {
                      if (newValue?.value) {
                        onOverviewAttrChange(newValue.value, 'league')
                      }
                    }
                  : undefined
              }
              required
              placeholder="SELECT LEAGUE"
            />
          ) : (
            <span>
              {activeLeague !== undefined
                ? `${activeLeague.name} ${activeLeague.id}`
                : onEmbedChange
                ? 'SELECT LEAGUE'
                : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
